import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './PromptPanel.css';

/**
 * AI prompt panel component
 * @param {Object} props - Component props
 * @param {string} props.prompt - Prompt content
 * @param {Function} props.onGeneratePrompt - Generate prompt handler
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.title - Panel title
 */
function PromptPanel({ prompt, onGeneratePrompt, isLoading, title }) {
  const [copied, setCopied] = useState(false);
  const promptRef = useRef(null);
  
  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => {
        setCopied(false);
      }, 2000);
      
      return () => clearTimeout(timeout);
    }
  }, [copied]);
  
  // Scroll to bottom when prompt changes
  useEffect(() => {
    if (promptRef.current && prompt) {
      promptRef.current.scrollTop = promptRef.current.scrollHeight;
    }
  }, [prompt]);
  
  // Copy prompt to clipboard
  const handleCopy = async () => {
    if (!prompt || !navigator.clipboard) return;
    
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };
  
  return (
    <div className="prompt-panel">
      <div className="prompt-header">
        <h3>{title || 'AI Prompt'}</h3>
        <div className="prompt-actions">
          <button 
            className="generate-btn" 
            onClick={onGeneratePrompt}
            disabled={isLoading}
          >
            {isLoading ? 'Generating...' : 'Generate Prompt'}
          </button>
          
          <button 
            className="copy-btn" 
            onClick={handleCopy} 
            disabled={!prompt}
          >
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>
      </div>
      
      <div className="prompt-content" ref={promptRef}>
        {prompt ? (
          <pre>{prompt}</pre>
        ) : (
          <div className="empty-prompt">
            Click "Generate Prompt" to create an AI prompt for the current phase.
          </div>
        )}
      </div>
      
      <div className="prompt-instructions">
        <h4>Instructions:</h4>
        <ol>
          <li>Click "Generate Prompt" to create a prompt for the current phase</li>
          <li>Click "Copy to Clipboard" to copy the prompt</li>
          <li>Paste the prompt to your AI assistant</li>
          <li>Copy the AI's response and paste it in the Response panel</li>
        </ol>
      </div>
    </div>
  );
}

PromptPanel.propTypes = {
  prompt: PropTypes.string,
  onGeneratePrompt: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  title: PropTypes.string
};

PromptPanel.defaultProps = {
  isLoading: false
};

export default PromptPanel;
